import { useCallback } from 'react';

import { getWindow } from '@change-corgi/core/window';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useVideoContext, useVideosComponentContext } from '../../../context';

type CameraAccess = {
	openCamera: () => Promise<MediaStream | null>;
	closeCamera: () => void;
};

const CONSTRAINTS = { audio: true, video: true } satisfies MediaStreamConstraints;

export function useCamera(): CameraAccess {
	const {
		data: { stream },
		actions: { setStream },
	} = useVideoContext();
	const {
		actions: { setErrorMessage },
	} = useVideosComponentContext();

	const { translate } = useI18n();
	const permissionExplanation = translate('corgi.petition_actions.supporter_voice_video.record.permission_explanation');

	const openCamera = useCallback(async () => {
		const { mediaDevices } = getWindow().navigator;
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (!mediaDevices) {
			setErrorMessage(permissionExplanation);
			return null;
		}

		try {
			const mediaStream = await mediaDevices.getUserMedia(CONSTRAINTS);
			setStream(mediaStream);
			setErrorMessage('');
			return mediaStream;
		} catch {
			setErrorMessage(permissionExplanation);
			return null;
		}
	}, [permissionExplanation, setErrorMessage, setStream]);

	const closeCamera = useCallback(() => {
		if (stream) {
			stream.getTracks().forEach((track) => {
				track.stop();
			});
		}
	}, [stream]);

	return { openCamera, closeCamera };
}
